import { useState, useEffect } from 'react'
import axios from '../utils/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { User, Shield, Ban, CheckCircle, Search } from 'lucide-react'

export default function ManageUsers() {
  const [users, setUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState('all') // all, admin, active, banned
  const [processing, setProcessing] = useState({})

  useEffect(() => {
    let cancelled = false

    const fetchUsers = async () => {
      try {
        setLoading(true)
        const auth = JSON.parse(localStorage.getItem('auth'))
        const token = auth?.token
        if (!token) {
          window.location.href = '/admin/login'
          return
        }
        const response = await axios.get('/admin/users', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (cancelled) return
        setUsers(response.data.users || [])
      } catch (error) {
        if (cancelled) return
        console.error('Error fetching users:', error)
        if (error.response?.status === 401) {
          window.location.href = '/admin/login'
        }
        alert('Failed to fetch users')
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchUsers()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    filterUsers()
  }, [searchTerm, filter, users])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      const response = await axios.get('/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setUsers(response.data.users || [])
    } catch (error) {
      console.error('Error fetching users:', error)
      if (error.response?.status === 401) {
        window.location.href = '/admin/login'
      }
      alert('Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }

  const filterUsers = () => {
    let filtered = users

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Apply status filter
    if (filter === 'admin') {
      filtered = filtered.filter(user => user.isAdmin)
    } else if (filter === 'active') {
      filtered = filtered.filter(user => !user.isBanned)
    } else if (filter === 'banned') {
      filtered = filtered.filter(user => user.isBanned)
    }

    setFilteredUsers(filtered)
  }

  const toggleAdmin = async (userId, currentStatus) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'remove admin' : 'make admin'} for this user?`)) {
      return
    }

    try {
      setProcessing({ ...processing, [userId]: true })
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      await axios.put(
        `/admin/users/${userId}`,
        { isAdmin: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      fetchUsers()
    } catch (error) {
      console.error('Error updating user:', error)
      if (error.response?.status === 401) {
        window.location.href = '/admin/login'
      }
      alert(error.response?.data?.error || 'Failed to update user')
    } finally {
      setProcessing({ ...processing, [userId]: false })
    }
  }

  const toggleBan = async (userId, currentStatus) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'unban' : 'ban'} this user?`)) {
      return
    }

    try {
      setProcessing({ ...processing, [userId]: true })
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      await axios.put(
        `/admin/users/${userId}`,
        { isBanned: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      fetchUsers()
    } catch (error) {
      console.error('Error updating user:', error)
      if (error.response?.status === 401) {
        window.location.href = '/admin/login'
      }
      alert(error.response?.data?.error || 'Failed to update user')
    } finally {
      setProcessing({ ...processing, [userId]: false })
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Manage Users</h1>
        <p className="text-muted-foreground mt-1">View and manage user accounts</p>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by username or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-input rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Filter Buttons */}
            <div className="flex gap-2">
              {['all', 'admin', 'active', 'banned'].map((filterType) => (
                <Button
                  key={filterType}
                  variant={filter === filterType ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(filterType)}
                  className="capitalize"
                >
                  {filterType}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      {loading ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-muted-foreground">Loading users...</p>
          </CardContent>
        </Card>
      ) : filteredUsers.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-muted-foreground">No users found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredUsers.map((user) => {
            const isProcessing = processing[user.id]
            return (
              <Card key={user.id}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <img
                        src={user.avatarUrl || `https://github.com/${user.username}.png`}
                        alt={user.username}
                        className="h-12 w-12 rounded-full shrink-0"
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold truncate">{user.username}</h3>
                          {user.isAdmin && (
                            <Badge variant="default" className="gap-1">
                              <Shield className="h-3 w-3" />
                              Admin
                            </Badge>
                          )}
                          {user.isBanned && (
                            <Badge variant="destructive" className="gap-1">
                              <Ban className="h-3 w-3" />
                              Banned
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{user.email || 'No email'}</p>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
                          <span>ID: {user.githubId}</span>
                          <span>Joined: {new Date(user.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                      <Button
                        variant={user.isAdmin ? 'destructive' : 'default'}
                        size="sm"
                        onClick={() => toggleAdmin(user.id, user.isAdmin)}
                        disabled={isProcessing}
                        className="gap-1 sm:gap-2"
                      >
                        <Shield className="h-4 w-4" />
                        <span className="hidden sm:inline">{user.isAdmin ? 'Remove Admin' : 'Make Admin'}</span>
                        <span className="sm:hidden">{user.isAdmin ? 'Unadmin' : 'Admin'}</span>
                      </Button>
                      <Button
                        variant={user.isBanned ? 'default' : 'destructive'}
                        size="sm"
                        onClick={() => toggleBan(user.id, user.isBanned)}
                        disabled={isProcessing}
                        className="gap-1 sm:gap-2"
                      >
                        {user.isBanned ? (
                          <>
                            <CheckCircle className="h-4 w-4" />
                            <span>Unban</span>
                          </>
                        ) : (
                          <>
                            <Ban className="h-4 w-4" />
                            <span>Ban</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Summary */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Showing {filteredUsers.length} of {users.length} users</span>
            <div className="flex gap-4">
              <span>{users.filter(u => u.isAdmin).length} admins</span>
              <span>{users.filter(u => u.isBanned).length} banned</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
